<template>
  <div v-if="currentStatus === 'PRE_UPLOAD'" class="status-container" :style="{ height: height }">
    <IAIProgress :variant="'RING'" :current="item.percentage || 0" :disabled="true" />

    <div class="details">
      <div class="file-name">{{ item.fileName }}</div>

      <div>{{ t("pages.sell-a-profile.preview.gallery.uploading") }}</div>
    </div>
  </div>

  <div v-else-if="currentStatus === 'UPLOADING'" class="status-container" :style="{ height: height }">
    <IAIProgress :variant="'RING'" :current="item.percentage || 0" />

    <div class="details">
      <div class="file-name">{{ item.fileName }}</div>

      <div>{{ t("pages.sell-a-profile.preview.gallery.uploading") }}.</div>
    </div>
  </div>

  <div
    v-if="currentStatus === 'PROCESSING' || currentStatus === 'PRE-PROCESSING'"
    class="status-container"
    :style="{ height: height }"
  >
    <IAILoader :variant="'PULSE'" :color="'disabled'" />

    <div>{{ t("pages.sell-a-profile.preview.gallery.processing") }}</div>
  </div>

  <div v-if="currentStatus === 'SUCCESS'" class="status-container" :style="{ height: height }">
    <div v-if="isLoading" class="status-container">
      <IAILoader :variant="'PULSE'" :color="'disabled'" />

      <div>{{ t("pages.sell-a-profile.preview.gallery.processing") }}</div>
    </div>

    <div v-show="!isLoading" class="relative h-full w-full cursor-pointer" @click="expandImage">
      <img
        :src="item.presignedUrl"
        class="image-item h-full w-full"
        @error="handleImageLoadingFailed"
        @load="handleImageLoad"
      />

      <div
        v-if="!isMobile"
        class="hover-layout absolute flex h-full w-full items-center justify-center text-sm font-medium"
      >
        {{ t("pages.sell-a-profile.preview.gallery.full-size") }}
      </div>
    </div>

    <div class="expanded-image-overlay" :class="showExpandedImageUrl && 'active'" @click="showExpandedImageUrl = false">
      <ExpandedImage
        v-if="item.presignedUrl"
        v-show="showExpandedImageUrl"
        :type="type"
        :is-shown="showExpandedImageUrl"
        :presigned-url="item.presignedUrl"
        :opposite-presigned-url="oppositeItem?.presignedUrl"
        :opposite-status="oppositeItem?.status || 'FAIL'"
        :file-name="item.fileName"
        @click.stop
        @close="showExpandedImageUrl = false"
      />
    </div>
  </div>

  <div v-if="currentStatus === 'FAIL'" class="status-container" :style="{ height: height }">
    <img :src="Icons.ExclaimCircle.src" />

    <div>{{ t("pages.sell-a-profile.preview.gallery.error") }}</div>
  </div>
</template>

<script setup lang="ts">
import { IAILoader, IAIProgress } from "@/core-ui-vue";
import { Icons } from "@/core-ui-vue";
import { ref, type PropType, Ref, watch } from "vue";
import { t } from "@/locales/vue";
import { computed } from "vue";
import type { PreviewPhotoItem } from "./UserPreviewGallery.vue";
import { logger } from "@/services/logger";
import ExpandedImage from "./ExpandedImage.vue";
import { useBreakpoints } from "@/features/checkout/v3/composables/useBreakpoints";
import { GalleryType } from "@/features/sell-a-profile/components/bundle-details/ProfilePreviewGallery.vue";

export type Status = "PRE_UPLOAD" | "UPLOADING" | "PRE-PROCESSING" | "PROCESSING" | "SUCCESS" | "FAIL";

const props = defineProps({
  item: { type: Object as PropType<PreviewPhotoItem>, required: true },
  oppositeItem: { type: Object as PropType<PreviewPhotoItem>, required: false },
  type: { type: String as PropType<GalleryType>, required: true },
  staticHeight: { type: String },
});

const emit = defineEmits<{
  isExpandedImage: [value: boolean];
}>();

const showExpandedImageUrl = ref(false);

const { isMobile } = useBreakpoints();

const expandImage = () => {
  if (isMobile.value) return;
  showExpandedImageUrl.value = true;
};

const isLoading = ref(true);
const setIsLoading = (newState: boolean) => {
  isLoading.value = newState;
};

const imageLoadingFailed = ref(false);
const setImageLoadingFailed = (newState: boolean) => {
  imageLoadingFailed.value = newState;
};

const currentStatus: Ref<Status> = computed(() => {
  if (props.item.status === "SUCCESS" && imageLoadingFailed.value) return "FAIL";
  else return props.item.status;
});

const handleImageLoadingFailed = () => {
  logger.error("GalleryItem -> image loading failed", {
    fileName: props.item.fileName,
    presignedUrl: props.item.presignedUrl,
  });
  setImageLoadingFailed(true);
};

const height = ref(props.staticHeight || "200px");

const handleImageLoad = () => {
  setIsLoading(false);
  if (!props.staticHeight) {
    height.value = "auto";
  }
};

watch(showExpandedImageUrl, (newValue) => emit("isExpandedImage", newValue));
</script>

<style scoped lang="scss">
.status-container {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  flex-direction: column;
  @apply bg-theme-placeholder-bg text-xs text-theme-1;
}

.details {
  text-align: center;
  width: 230px;
  .file-name {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}
.image-item {
  object-fit: cover;
}

.hover-layout {
  opacity: 0;
  color: white;
  top: 0;
  z-index: 2;
  transition: all 0.5s;
  &:hover {
    background-color: rgba(0, 0, 0, 0.2);
    opacity: 1;
  }
}

.expanded-image-overlay {
  position: fixed;
  z-index: 100;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  opacity: 0;
  visibility: hidden;
  transition:
    opacity 0.3s ease-in-out,
    visibility 0.3s ease-in-out;
}
.image-container {
  max-width: 80%;
  max-height: 90%;
  position: relative;
  .close-button {
    position: absolute;
    right: -1.75rem;
    top: -1.75rem;
    filter: invert(100%) sepia(0%) saturate(0%) hue-rotate(285deg) brightness(102%) contrast(103%);
  }
}
.expanded-image {
  max-width: 100%;
  max-height: 100%;
  animation: fadeIn 0.3s ease-in-out;
}
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.expanded-image-overlay.active {
  opacity: 1;
  visibility: visible;
}
</style>
